"use client";

import { useEffect, useRef, type ReactNode } from "react";

/* La coda della colonna destra della pagina di lettura, quando non ci sono
   riempitivi (pezzi di lib/editoriali.ts, editoriali riservati).

   Richiesta del 04/11/2026 su «Il mercato speculativo»: la colonna (scheda,
   «Le verità costruite», tre editoriali precedenti) era più lunga del testo
   e sotto la fine del testo restava il bianco. «Se il testo è finito togli
   il card: metti i card quando c'è testo, non quando non c'è.»

   Da `lg` la colonna resta `sticky` e alta quanto il suo contenuto (dove il
   testo è più lungo continua a scorrere accanto a lui, come prima); se però
   supera l'altezza del testo, si tolgono dal fondo i riquadri finché non la
   supera più: prima le card dei precedenti, dall'ultima, poi il gruppo
   rimasto vuoto col suo titolo, poi «Le verità costruite». La scheda resta
   sempre. Si confrontano le altezze (`offsetHeight`) e non le posizioni:
   la `sticky` sposta le posizioni mentre si scorre, non le altezze.

   Gli elementi candidati portano `data-coda`; un contenitore con
   `data-gruppo` sparisce quando non gli resta nessun `data-coda` visibile.
   Sotto `lg` si vede tutto, come prima. */
export function CodaColonna({ children }: { children: ReactNode }) {
  const radice = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = radice.current;
    const colonna = el?.closest("aside") as HTMLElement | null;
    const testo = colonna?.previousElementSibling as HTMLElement | null;
    if (!el || !colonna || !testo) return;
    const largo = window.matchMedia("(min-width: 64rem)");
    const adatta = () => {
      const voci = [...el.querySelectorAll<HTMLElement>("[data-coda]")];
      const gruppi = [...el.querySelectorAll<HTMLElement>("[data-gruppo]")];
      [...voci, ...gruppi].forEach((v) => (v.style.display = ""));
      if (!largo.matches) return;
      const sfora = () => colonna.offsetHeight > testo.offsetHeight + 1;
      for (let i = voci.length - 1; i >= 0 && sfora(); i--) {
        voci[i].style.display = "none";
        for (const g of gruppi) {
          const viste = [...g.querySelectorAll<HTMLElement>("[data-coda]")].some((v) => v.style.display !== "none");
          if (!viste) g.style.display = "none";
        }
      }
    };
    adatta();
    // Si osserva il testo (caratteri, foto, larghezza) e non la colonna,
    // che cambia proprio perché qui si tolgono le schede.
    const ro = new ResizeObserver(adatta);
    ro.observe(testo);
    largo.addEventListener("change", adatta);
    document.fonts?.ready.then(adatta);
    return () => {
      ro.disconnect();
      largo.removeEventListener("change", adatta);
    };
  }, []);

  return (
    <div ref={radice} className="flex flex-col gap-10">
      {children}
    </div>
  );
}
